import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { type User } from '@/types/user';
import { AlertTriangle } from 'lucide-react';

interface DeleteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
  users: User[];
  onDeleteUser: (userId: string, reassignToUserId?: string) => void;
}

export function DeleteUserDialog({
  open,
  onOpenChange,
  user,
  users,
  onDeleteUser,
}: DeleteUserDialogProps) {
  const [reassignAccounts, setReassignAccounts] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');

  const handleDelete = () => {
    if (user) {
      onDeleteUser(user.id, reassignAccounts ? selectedUserId : undefined);
      handleClose();
    }
  };

  const handleClose = () => {
    setReassignAccounts(false);
    setSelectedUserId('');
    onOpenChange(false);
  };

  if (!user) return null;

  // Filter out the current user from the reassignment list
  const availableUsers = users.filter((u) => u.id !== user.id && u.status === 'active');

  return (
    <AlertDialog open={open} onOpenChange={handleClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-destructive" />
            </div>
            <div>
              <AlertDialogTitle>Delete User</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete {user.firstName} {user.lastName}?
              </AlertDialogDescription>
            </div>
          </div>
        </AlertDialogHeader>

        <div className="space-y-4 py-4">
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">
              This action cannot be undone. The user will be permanently removed from the
              system.
            </p>
          </div>

          {user.companyCount > 0 && (
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="reassign"
                  checked={reassignAccounts}
                  onCheckedChange={(checked) => {
                    setReassignAccounts(checked as boolean);
                    if (!checked) {
                      setSelectedUserId('');
                    }
                  }}
                />
                <Label htmlFor="reassign" className="text-sm font-medium cursor-pointer">
                  Reassign user's accounts to another user
                </Label>
              </div>

              {reassignAccounts && (
                <div className="space-y-2 pl-6">
                  <Label htmlFor="reassign-user" className="text-sm">
                    Select user to reassign accounts to:
                  </Label>
                  <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                    <SelectTrigger id="reassign-user">
                      <SelectValue placeholder="Select a user..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableUsers.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.firstName} {u.lastName} ({u.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {availableUsers.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      No active users available for reassignment
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={reassignAccounts && !selectedUserId}
          >
            Delete User
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
